import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, analyticsAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await authAPI.me();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Heartbeat polling — sends activity ping every 2 minutes for logged-in users
  const heartbeatRef = useRef(null);
  useEffect(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (!user) return;
    analyticsAPI.heartbeat().catch(() => {});
    heartbeatRef.current = setInterval(() => {
      analyticsAPI.heartbeat().catch(() => {});
    }, 120000);
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, city) => {
    const { data } = await authAPI.register({ email, password, name, city });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await authAPI.logout();
    setUser(null);
  };

  const googleLogin = async (sessionId) => {
    const { data } = await authAPI.googleSession(sessionId);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, googleLogin, setUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
